/**
 * Phase 45 Plan 45-06 — Log-time popover.
 *
 * Anchored to a MyTaskRow's "+ Log" button per UI-SPEC §Log-time popover
 * (lines 408-415). Mirrors the focus-trap + Esc revert + outside-click +
 * body-scroll-lock semantics from TaskDetailSlideOver (Phase 41 P17).
 *
 * Interaction:
 *   - 6 quick-pick chips +0.25h / +0.5h / +1h (default) / +2h / +4h / +8h.
 *   - Free-text input "What did you work on?" (optional description).
 *   - Save button calls createEntry({ projectId, date: today, hours,
 *     description }); on success closes the popover + toasts + invalidates
 *     ['tasks','mine'] + ['entries'].
 *   - Esc closes; outside-click closes; rapid double-click is debounced
 *     via the `isPending` guard on the mutation.
 *
 * The popover is rendered inside an absolutely positioned wrapper that is
 * attached to the row's relative container (see MyTaskRow.tsx); this file
 * owns the panel content + behaviour only.
 */
import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type FormEvent,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ClockIcon } from '@heroicons/react/24/outline';
import { createEntry } from '../../services/api/timeEntry';
import { useToast } from '../ui/Toast';
import { formatDayMonthShort, formatWeekdayShort } from '../../utils/dateFormat';

const QUICK_PICKS: number[] = [0.25, 0.5, 1, 2, 4, 8];
const DEFAULT_PICK = 1;

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function formatTodayLabel(): string {
  // MKS-351 — "Today · Mon 20 Nov", English weekday and month.
  const now = new Date();
  return `Today · ${formatWeekdayShort(now)} ${formatDayMonthShort(now)}`;
}

export interface LogTimePopoverProps {
  taskId: number;
  taskName: string;
  projectId: number;
  open: boolean;
  onClose: () => void;
  /**
   * Optional external override. Tests pass a stub to assert popover behaviour
   * without booting an actual `useMutation` wrapper.
   */
  onSubmit?: (input: { projectId: number; date: string; hours: number; description: string }) => Promise<void>;
}

export function LogTimePopover({
  // Plan 45-09 Rule 1 lint fix — taskId is part of the prop contract for
  // future per-task entry endpoints; prefix with `_` so the unused-vars
  // ESLint rule passes while the prop name stays in the public type.
  taskId: _taskId,
  taskName,
  projectId,
  open,
  onClose,
  onSubmit,
}: LogTimePopoverProps) {
  const [hours, setHours] = useState<number>(DEFAULT_PICK);
  const [description, setDescription] = useState<string>('');
  const headingId = useId();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const firstChipRef = useRef<HTMLButtonElement | null>(null);
  const queryClient = useQueryClient();
  const toast = useToast();

  const mutation = useMutation({
    mutationFn: async (input: { projectId: number; hours: number; description: string }) => {
      if (onSubmit) {
        await onSubmit({
          projectId: input.projectId,
          date: todayIso(),
          hours: input.hours,
          description: input.description,
        });
        return;
      }
      await createEntry({
        projectId: input.projectId,
        date: todayIso(),
        hours: input.hours,
        description: input.description || undefined,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks', 'mine'] });
      queryClient.invalidateQueries({ queryKey: ['entries'] });
      toast(`Logged ${hours}h on ${taskName}`, 'success');
      setDescription('');
      setHours(DEFAULT_PICK);
      onClose();
    },
    onError: (err) => {
      const msg = err instanceof Error ? err.message : 'Failed to log time';
      toast(msg, 'error');
    },
  });

  // Reset state on open.
  useEffect(() => {
    if (open) {
      setHours(DEFAULT_PICK);
      setDescription('');
    }
  }, [open]);

  // Initial focus → first quick-pick chip after the panel mounts.
  useEffect(() => {
    if (!open) return;
    const raf = window.requestAnimationFrame(() => {
      firstChipRef.current?.focus();
    });
    return () => window.cancelAnimationFrame(raf);
  }, [open]);

  // Document-level Escape listener + outside-click close.
  useEffect(() => {
    if (!open) return;

    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    }

    function handleMouseDown(e: MouseEvent) {
      const container = containerRef.current;
      if (!container) return;
      if (!container.contains(e.target as Node)) onClose();
    }

    document.addEventListener('keydown', handleKey, true);
    document.addEventListener('mousedown', handleMouseDown);
    return () => {
      document.removeEventListener('keydown', handleKey, true);
      document.removeEventListener('mousedown', handleMouseDown);
    };
  }, [open, onClose]);

  // Focus trap — Tab/Shift+Tab wraps within the popover.
  const handlePanelKeyDown = useCallback((e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Tab') return;
    const panel = containerRef.current;
    if (!panel) return;
    const focusables = Array.from(
      panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
    ).filter((el) => !el.hasAttribute('disabled'));
    if (focusables.length === 0) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (!first || !last) return;
    const active = document.activeElement as HTMLElement | null;
    if (e.shiftKey && active === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  }, []);

  if (!open) return null;

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (mutation.isPending) return; // rapid double-click guard
    mutation.mutate({ projectId, hours, description: description.trim() });
  }

  return (
    <div
      ref={containerRef}
      role="dialog"
      aria-label="Log time"
      aria-labelledby={headingId}
      onKeyDown={handlePanelKeyDown}
      data-testid="log-time-popover"
      className="absolute right-0 top-full mt-1 z-40 w-72 bg-ms-surface border border-ms-border rounded-lg shadow-xl p-3"
    >
      <form onSubmit={handleSubmit}>
        <div className="flex items-center gap-2 mb-2">
          <ClockIcon className="h-3.5 w-3.5 text-ms-text-secondary" aria-hidden="true" />
          <span id={headingId} className="text-[11px] font-semibold text-ms-text-strong">
            Log time
          </span>
          <button
            type="button"
            onClick={onClose}
            className="ml-auto text-ms-text-muted hover:text-ms-text-strong text-[11px]"
            aria-label="Close popover"
            data-testid="log-time-popover-close"
          >
            Esc
          </button>
        </div>

        <div className="text-[11px] text-ms-text-muted mb-2 truncate" title={taskName}>
          {taskName}
        </div>

        <div className="flex items-center gap-1.5 mb-2" role="group" aria-label="Quick pick hours">
          {QUICK_PICKS.map((h, idx) => {
            const active = h === hours;
            return (
              <button
                key={h}
                type="button"
                ref={idx === 0 ? firstChipRef : null}
                onClick={() => setHours(h)}
                aria-pressed={active}
                data-testid={`log-time-chip-${h}`}
                className={`flex-1 py-1 text-[11px] font-semibold border rounded ${
                  active
                    ? 'bg-ms-orange/10 border-ms-orange text-ms-orange'
                    : 'hover:bg-ms-surface-2 text-ms-text-secondary border-ms-border-subtle'
                }`}
              >
                +{h}h
              </button>
            );
          })}
        </div>

        <input
          type="text"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="What did you work on?"
          aria-label="What did you work on?"
          className="w-full bg-ms-surface-2/60 border border-ms-border-subtle rounded px-2 py-1.5 text-[11px] text-ms-text-secondary mb-2 focus:outline-none focus:ring-2 focus:ring-ms-orange/30"
          data-testid="log-time-description"
        />

        <div className="flex items-center gap-2">
          <span className="text-[10.5px] text-ms-text-muted tabular-nums">
            {formatTodayLabel()}
          </span>
          <button
            type="submit"
            disabled={mutation.isPending}
            data-testid="log-time-save"
            className="ml-auto px-2.5 py-1 text-[11px] font-semibold rounded text-white bg-ms-orange disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Save · {hours}h
          </button>
        </div>
      </form>
    </div>
  );
}
